import {
  searchParametersActions,
  selectResultTypeAndSorting,
  selectSorting,
} from 'features/searchResultsFacets';
import Select from '@components/select';
import { store, store$ } from '@redux/store';
import {
  map,
  distinctUntilKeyChanged,
  skip,
  filter,
  distinctUntilChanged,
} from 'rxjs';

const sortElement = document.querySelector('[data-sort-element]');

const select$ = new Select(sortElement).getObservable();

select$.subscribe((value) => {
  const sortBy = value.split(',')[0];
  const sortOrder = value.split(',')[1] === 'true';
  store.dispatch(
    searchParametersActions.fetchSort({
      sortBy,
      sortOrder,
    })
  );
});

/**
 * This observable will emit values every time there is a change in the result type.
 */
const onResultsTypeChanged$ = store$.pipe(
  map(selectResultTypeAndSorting),
  distinctUntilKeyChanged('resultType'),
  map(({sorting, resultType}) => ({
      value: `${sorting.sortBy},${sorting.sortOrder}`,
      resultType,
    })),
  skip(1),
);

/**
 * updateOptionsVisibility: based on the current result type, this function updates the visibility
 * of some options that are unique for a given result type and should not appear in all scenarios.
 * @param {string} resultType is the result type name. 
 */
const updateOptionsVisibility = ({resultType}) => {
  const uniqueOptions = sortElement.querySelectorAll(`[data-only-result-types]`);
  uniqueOptions.forEach((option) => {
    const validResultTypes = option.dataset.onlyResultTypes.split(',');
    if (validResultTypes.includes(resultType)) {
      option.removeAttribute('hidden');
    } else {
      option.setAttribute('hidden', '');
    }
  });
}

onResultsTypeChanged$.subscribe(updateOptionsVisibility);

/**
 * synchronizeSortElement: there are scenarios where users might sort results using
 * a sorting criteria that is unique for the result type they are currently viewing.
 * For example: the sorting criteria "bed" is only available for "homes" result type,
 * so if the user selects it and then navigates to another result type, the select HTML
 * element has to be congruent with the user selection and the results shown.
 * In the specified example described, the sorting will change to "default" as "bed" only exists
 * for "homes" result type, so this function is in charge of reflecting that change in the
 * select HTML element the user is seeing in the newly selected result type.
 * @param {HTML} sortElement is the HTML control the user is currently seeing. 
 * @param {string} value represents the value to be selected in the HTML control. 
 */
const synchronizeSortElement = ({value}) => {
  if (sortElement.querySelector(`option[value="${value}"]`)) {
    sortElement.value = value;
    return;
  }

  sortElement.selectedIndex = 0;
}

onResultsTypeChanged$.subscribe(synchronizeSortElement);

/**
 * onSortStateChange$: is an observable of the store. The streams will occur whenever the
 * state value for the sorting is different than what the sorting HTML is showing.
 * This could happen when you have applied a sorting option, and then you clean up the filters,
 */
const onSortStateChange$ = store$.pipe(
  map(selectSorting),
  distinctUntilChanged(),
  map((sorting) => ({
    value: `${sorting?.sortBy},${sorting?.sortOrder}`,
    sortElementValue: sortElement.value,
  })),
  filter(({value, sortElementValue}) => value !== sortElementValue),
);

onSortStateChange$.subscribe(synchronizeSortElement);